// Angular
import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

// Component Library
import { FsxTableRowStatus } from 'fsx-component-library';

// Data Contracts
import { ClerkReviewListResponse, ClerkReviewQueryRequest } from '@data-contracts';

// Local
import { ComparableArrayDataService } from '@app/abstract-classes/comparable-array-data-service';
import { ReviewFilingSummaryWithStatus } from '@feature/crl/clerk-review-list-data.service';
import { ClerkEditedDocumentTitle, applyClerkEditedTitlesToSummaries, dropDocumentTitleOverridesCaughtUpOnServer, readDocumentTitleOverridesFromSession, writeDocumentTitleOverridesToSession } from '@feature/crl/services/map-filing-summaries-document-titles.utils';

/**
 * A interface combining filing summaries and the applied payload.
 */
export interface FilingSummariesAndAppliedPayload {
  /**
   * The filing summaries with the FsxTableRowStatus.
   */
  filingSummariesWithStatus: ReviewFilingSummaryWithStatus[];

  /**
   * The applied payload used to retrieve the filing summaries.
   */
  appliedPayload: ClerkReviewQueryRequest;
}

/**
 * A service to manage the filing summaries data.
 */
@Injectable({ providedIn: 'root' })
export class FilingSummariesDataService extends ComparableArrayDataService<ReviewFilingSummaryWithStatus> {
  /**
   * Clerk-edited titles keyed by filing unique ID, then document ID.
   * Hydrated from sessionStorage so a same-tab refresh still overlays a
   * stale `documentTitle` from the list query.
   */
  private readonly documentTitleOverridesByFiling =
    readDocumentTitleOverridesFromSession();

  /**
   * A BehaviorSubject to store the total number of transactions.
   */
  private readonly totalTransactions$$ = new BehaviorSubject<number>(0);

  /**
   * A BehaviorSubject to store the applied payload.
   */
  private readonly appliedPayload$$ = new BehaviorSubject<ClerkReviewQueryRequest | null>(null);

  /**
   * An Observable of the filing summaries and the applied payload.
   */
  filingSummariesAndPayload$: Observable<FilingSummariesAndAppliedPayload> = combineLatest([
    this.data$,
    this.appliedPayload$$.asObservable()
  ]).pipe(
    map(([filingSummariesWithStatus, appliedPayload]) => ({ filingSummariesWithStatus, appliedPayload }))
  );

  /**
   * A getter to return the total number of transactions as an Observable.
   */
  get totalTransactions$(): Observable<number> {
    return this.totalTransactions$$.asObservable();
  }

  /**
   * A getter to return the total number of transactions.
   */
  get totalTransactions(): number {
    return this.totalTransactions$$.value;
  }

  /**
   * A method to set the filing summaries and the total number of transactions.
   * 
   * @param response - The response object from the API.
   */
  setFilingSummaries(response: ClerkReviewListResponse, payload: ClerkReviewQueryRequest): void {
    // Map the filing summaries to include the FsxTableRowStatus
    const filingSummariesWithStatus = (response.data || []).map(summary => ({
      ...summary,
      fsxTableRowStatus: summary.isLate ? FsxTableRowStatus.Danger : FsxTableRowStatus.None
    }));

    // Set the filing summaries
    super.setData(this.applyDocumentTitleOverrides(filingSummariesWithStatus));

    // Set the applied payload
    this.appliedPayload$$.next(payload);

    // Set the total number of transactions
    this.totalTransactions$$.next(response.count || 0);
  }

  /**
   * Overwrites `documentTitle` on the cached list row that matches the saved filing.
   * Detail save persists `ClerkReviewDocument.title`; the list grid reads `documentTitle`.
   */
  updateDocumentTitlesForFiling(
    filingUniqueId: string | null | undefined,
    documents: readonly ClerkEditedDocumentTitle[],
  ): void {
    if (!filingUniqueId) {
      return;
    }

    this.rememberDocumentTitleOverrides(filingUniqueId, documents);

    const current = this.data;
    if (!current?.length) {
      return;
    }

    super.setData(
      applyClerkEditedTitlesToSummaries(current, filingUniqueId, documents),
    );
  }

  /**
   * Drops in-memory and session overlays so the next login or emulated user
   * in this tab cannot inherit another clerk's saved titles.
   */
  clearDocumentTitleOverrides(): void {
    this.documentTitleOverridesByFiling.clear();
    writeDocumentTitleOverridesToSession(this.documentTitleOverridesByFiling);
  }

  private rememberDocumentTitleOverrides(
    filingUniqueId: string,
    documents: readonly ClerkEditedDocumentTitle[],
  ): void {
    const titlesByDocumentId = new Map<number, string>();
    for (const document of documents) {
      if (document.documentId == null || document.title == null) {
        continue;
      }
      titlesByDocumentId.set(document.documentId, document.title);
    }

    if (titlesByDocumentId.size === 0) {
      return;
    }

    this.documentTitleOverridesByFiling.set(filingUniqueId, titlesByDocumentId);
    writeDocumentTitleOverridesToSession(this.documentTitleOverridesByFiling);
  }

  private applyDocumentTitleOverrides(
    summaries: ReviewFilingSummaryWithStatus[],
  ): ReviewFilingSummaryWithStatus[] {
    if (this.documentTitleOverridesByFiling.size === 0) {
      return summaries;
    }

    if (
      dropDocumentTitleOverridesCaughtUpOnServer(
        this.documentTitleOverridesByFiling,
        summaries,
      )
    ) {
      writeDocumentTitleOverridesToSession(this.documentTitleOverridesByFiling);
    }

    if (this.documentTitleOverridesByFiling.size === 0) {
      return summaries;
    }

    let result = summaries;
    for (const [filingUniqueId, titles] of this.documentTitleOverridesByFiling) {
      const documents = [...titles.entries()].map(([documentId, title]) => ({
        documentId,
        title,
      }));
      result = applyClerkEditedTitlesToSummaries(
        result,
        filingUniqueId,
        documents,
      );
    }
    return result;
  }
} 